import { createRouter, createWebHistory } from 'vue-router'
import type { Router } from 'vue-router'
import { i18n } from '@/i18n'
import { useNotificationsStore } from '@/stores/notifications'
import { useTenantStore } from '@/stores/tenant'
import { authGuard, featureGuard } from './guards'

declare module 'vue-router' {
  interface RouteMeta {
    /* i18n key for the document title */
    titleKey?: string
    requiresAuth?: boolean
    requiresFeature?: 'styleguide'
    /* dev documentation route: reachable in a dev build even if the feature
       flag is off (see featureGuard) */
    devOnly?: boolean
    /* reachable without authentication (e.g. the OIDC callback) */
    public?: boolean
    layout?: 'default' | 'auth'
  }
}

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    /* Start each navigation at the top of the page (otherwise the browser keeps
       the previous scroll position - e.g. tapping a nav link after scrolling
       down lands you mid-page). Back/forward restores the remembered position;
       an in-page #hash still scrolls to its target. */
    scrollBehavior(to, _from, savedPosition) {
      if (savedPosition) return savedPosition
      if (to.hash) return { el: to.hash, top: 0 }
      return { top: 0 }
    },
    routes: [
      {
        path: '/',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { titleKey: 'dashboard.title', requiresAuth: true, layout: 'default' },
      },
      {
        path: '/auth/callback',
        name: 'callback',
        component: () => import('@/pages/CallbackPage.vue'),
        meta: { titleKey: 'callback.title', public: true, layout: 'auth' },
      },
      {
        path: '/bestellung',
        name: 'new-order',
        component: () => import('@/pages/NewOrderPage.vue'),
        meta: { titleKey: 'newOrder.title', requiresAuth: true, layout: 'default' },
      },
      {
        path: '/bestellungen',
        name: 'orders',
        component: () => import('@/pages/OrdersPage.vue'),
        meta: { titleKey: 'orders.title', requiresAuth: true, layout: 'default' },
      },
      {
        path: '/stammdaten',
        name: 'master-data',
        component: () => import('@/pages/MasterDataPage.vue'),
        meta: { titleKey: 'masterData.title', requiresAuth: true, layout: 'default' },
      },
      {
        path: '/downloads',
        name: 'downloads',
        component: () => import('@/pages/DownloadsPage.vue'),
        meta: { titleKey: 'downloads.title', requiresAuth: true, layout: 'default' },
      },
      {
        /* Dev documentation only: hidden in nav (feature flag off) and blocked
           by featureGuard in production builds; reachable in dev via devOnly. */
        path: '/styleguide',
        name: 'styleguide',
        component: () => import('@/pages/StyleguidePage.vue'),
        meta: {
          titleKey: 'styleguide.title',
          requiresFeature: 'styleguide',
          devOnly: true,
          layout: 'default',
        },
      },
      {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFoundPage.vue'),
        meta: { titleKey: 'notFound.title', layout: 'default' },
      },
    ],
  })

  router.beforeEach(authGuard)
  router.beforeEach(featureGuard)

  router.afterEach((to) => {
    const t = i18n.global.t
    const base = t(`portal.${useTenantStore().current.id}`)
    document.title = to.meta.titleKey ? `${t(to.meta.titleKey)} - ${base}` : base
  })

  /* A deploy replaces hashed chunk files; clients holding the old index.html
     then fail to lazy-load routes. Reload to pick up the new build, at most
     once per 30s so a persistently broken chunk cannot cause reload churn.
     Error wording per engine: Chrome "Failed to fetch dynamically imported
     module", Firefox "error loading dynamically imported module", Safari
     "Importing a module script failed". */
  router.onError((error, to) => {
    const isChunkError =
      /Failed to fetch dynamically imported module|error loading dynamically|Importing a module script failed/i.test(
        String(error),
      )
    if (isChunkError) {
      const key = 'partner.chunk-reload-at'
      const last = Number(sessionStorage.getItem(key) ?? 0)
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(key, String(Date.now()))
        window.location.assign(to.fullPath)
      } else {
        // Already reloaded once recently and still failing: tell the user
        // rather than leaving them on a silently broken page.
        useNotificationsStore().notify('error', 'errors.chunkReload')
      }
    }
  })

  return router
}
